import {
    CONFERENCE_JOINED,
    CONFERENCE_LEFT,
    ENDPOINT_MESSAGE_RECEIVED,
    getCurrentConference
} from '../base/conference';
import { setActiveModalId } from '../base/modal';
import {
    PARTICIPANT_JOINED,
    PARTICIPANT_LEFT,
    getParticipantDisplayName,
    getRemoteParticipants
} from '../base/participants';
import { MiddlewareRegistry } from '../base/redux';
import { checkIfReactNative } from '../base/util/checkOS';
import { OPEN_CHAT } from '../chat/actionTypes';
import { closeChat } from '../chat/actions.any';
import { IS_ENABLED } from '../clickandpledge-givent/actionTypes';
import { isClickandPledge } from '../clickandpledge-givent/actions';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../notifications';
import {
    MODERATION_PANE_OPEN,
    OPEN_PARTICIPANTS_PANE_MODAL,
    PARTICIPANTS_PANE_OPEN
} from '../participants-pane/actionTypes';
import {
    closeModerationPane,
    close as closeParticipantsPane,
    closeParticipantsPaneModal
} from '../participants-pane/actions';

import {
    CLOSE_NOTES_MODAL,
    FLUSH_NOTES,
    LOAD_CONFERENCE_NOTES,
    NOTES_MODAL_ID,
    NOTES_PANE_CLOSE,
    NOTES_PANE_OPEN,
    NOTES_SAVE_STATUS,
    OPEN_NOTES_MODAL,
    SET_NOTES_SHARED_WITH,
    SET_NOTES_TEXT,
    SHARED_NOTES_EDIT_RESULT,
    SHARED_NOTES_EDIT_SUBMIT,
    SHARED_NOTES_RECEIVED,
    SHARED_NOTES_REMOVED
} from './actionTypes';
import {
    closeNotesModal,
    closeNotesPane,
    loadNotesFromStorage,
    openNotesModal,
    openNotesPane,
    setNotesText,
    viewSharedNotes
} from './actions';
import {
    EDIT_RESULT_ACCEPTED,
    EDIT_RESULT_CONFLICT,
    EDIT_RESULT_FAILED,
    EDIT_RESULT_PENDING,
    EDIT_RESULT_REJECTED,
    EDIT_TIMEOUT_MS,
    MAX_SHARED_NOTES_LENGTH,
    NOTES_EDIT_MESSAGE,
    NOTES_EDIT_RESULT_MESSAGE,
    NOTES_SHARE_MESSAGE,
    NOTES_UNSHARE_MESSAGE,
    SHARE_AUDIENCE_EVERYONE,
    SHARE_AUDIENCE_SELECTED,
    SHARE_PERMISSION_EDIT,
    SHARE_PERMISSION_VIEW
} from './constants';
import {
    getNotesModalOpen,
    getNotesPaneOpen,
    getNotesRoomName,
    loadConferenceNotes,
    mergeNotes,
    saveConferenceNotes
} from './functions';
import logger from './logger';

const SAVE_DEBOUNCE_MS = 400;

// Viewers get the latest notes at most this often while the author types.
const SHARE_THROTTLE_MS = 1500;

let saveTimer;
let shareTimer;
let shareTooLargeShown = false;
const pendingSaves = new Map();

// ownerId -> timeout for an edit request that has not resolved yet.
const editTimers = new Map();

// revision -> document we sent, so an edit based on an older revision can be
// merged instead of rejected. Only the last few matter (edits time out).
const sentRevisions = new Map();
const MAX_SENT_REVISIONS = 50;

// Edit requests already applied; a replay would merge the same change twice.
const appliedEditRequests = new Set();

/**
 * Remembers the document recipients now hold as `revision`.
 *
 * @param {Object} notes - The notes feature state.
 * @returns {void}
 */
function rememberSentRevision({ revision, richText, text }) {
    sentRevisions.delete(revision);
    sentRevisions.set(revision, { richText,
        text });
    if (sentRevisions.size > MAX_SENT_REVISIONS) {
        sentRevisions.delete(sentRevisions.keys().next().value);
    }
}

/**
 * Cancels an owner's edit-request timeout, if one is armed.
 *
 * @param {string} ownerId - The owner of the shared notes.
 * @returns {void}
 */
function clearEditTimer(ownerId) {
    clearTimeout(editTimers.get(ownerId));
    editTimers.delete(ownerId);
}

/**
 * Cancels every armed edit-request timeout (room switches).
 *
 * @returns {void}
 */
function clearEditTimers() {
    for (const ownerId of editTimers.keys()) {
        clearEditTimer(ownerId);
    }
}

/**
 * Ids of the live remote non-fake participants of the current room. They are
 * the only valid share recipients and message senders.
 *
 * @param {Object} state - Global state.
 * @returns {Array<string>}
 */
function getShareableIds(state) {
    return getRemoteParticipants(state).map(p => p.id);
}

/**
 * Resolves the actual sender id of an endpoint message. Only a live remote
 * participant of the current room counts, and when the participant object
 * knows its conference it must be the one we are joined to — a payload claim
 * alone is never trusted.
 *
 * @param {Object} state - Global state.
 * @param {Object} participant - The lib participant that sent the message.
 * @returns {?string} The sender id, or null when it cannot be trusted.
 */
function getValidMessageSender(state, participant) {
    const conference = getCurrentConference(state);

    if (!conference) {
        return null;
    }
    const participantId = participant?.getId?.();

    if (!participantId || !getShareableIds(state).includes(participantId)) {
        return null;
    }
    const senderConference = participant?.getConference?.();

    return senderConference && senderConference !== conference ? null : participantId;
}

/**
 * The permission a participant currently holds on our shared notes, or null
 * when the notes are not shared with them at all.
 *
 * @param {Object} notes - The notes feature state.
 * @param {string} participantId - The participant id.
 * @returns {?string} 'view', 'edit', or null.
 */
function getSharePermission(notes, participantId) {
    if (!notes.sharedWith.includes(participantId)) {
        return null;
    }
    if (notes.shareAudience === SHARE_AUDIENCE_EVERYONE) {
        return notes.everyonePermission === SHARE_PERMISSION_EDIT ? SHARE_PERMISSION_EDIT : SHARE_PERMISSION_VIEW;
    }

    return notes.sharePermissions[participantId] === SHARE_PERMISSION_EDIT ? SHARE_PERMISSION_EDIT : SHARE_PERMISSION_VIEW;
}

/**
 * Sends one endpoint message without letting a signaling failure blow up the
 * middleware.
 *
 * @param {Object} conference - The current conference.
 * @param {Object} message - The payload.
 * @param {string} to - The recipient id.
 * @returns {boolean} Whether the send call succeeded.
 */
function safeSendMessage(conference, message, to) {
    try {
        conference.sendMessage(message, to);

        return true;
    } catch (error) {
        logger.warn('Failed to send notes message', error);

        return false;
    }
}

/**
 * Whether the current notes document fits one stanza.
 *
 * @param {Object} notes - The notes feature state.
 * @returns {boolean}
 */
function isShareWithinBudget(notes) {
    return JSON.stringify({ richText: notes.richText,
        text: notes.text }).length <= MAX_SHARED_NOTES_LENGTH;
}

/**
 * Sends the current notes to one recipient with the permission they hold.
 *
 * @param {Store} store - Redux store.
 * @param {string} participantId - The recipient.
 * @returns {void}
 */
function sendSharedNotesTo({ dispatch, getState }, participantId) {
    const state = getState();
    const conference = getCurrentConference(state);
    const notes = state['features/notes'];

    if (!conference || !notes.sharedWith.includes(participantId) || !isShareWithinBudget(notes)) {
        return;
    }
    rememberSentRevision(notes);
    if (!safeSendMessage(conference, { type: NOTES_SHARE_MESSAGE,
        permission: getSharePermission(notes, participantId) || SHARE_PERMISSION_VIEW,
        revision: notes.revision,
        richText: notes.richText,
        text: notes.text }, participantId)) {
        dispatch(showNotification({ titleKey: 'notesPane.shareFailed' }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
    }
}

/**
 * Sends the current notes to everyone they are shared with, each as a private
 * message so no other participant receives them.
 *
 * @param {Store} store - Redux store.
 * @returns {void}
 */
function sendSharedNotes({ dispatch, getState }) {
    clearTimeout(shareTimer);
    shareTimer = undefined;
    const state = getState();
    const conference = getCurrentConference(state);
    const notes = state['features/notes'];

    if (!conference || !notes.sharedWith.length) {
        return;
    }
    if (!isShareWithinBudget(notes)) {
        // Once per crossing, not on every throttled send while typing.
        if (!shareTooLargeShown) {
            shareTooLargeShown = true;
            dispatch(showNotification({ titleKey: 'notesPane.shareTooLarge' }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        }

        return;
    }
    shareTooLargeShown = false;
    rememberSentRevision(notes);

    // sendMessage tags the object it is given, so each recipient gets a fresh one.
    let failed = false;

    notes.sharedWith.forEach(id => {
        failed = !safeSendMessage(conference, { type: NOTES_SHARE_MESSAGE,
            permission: getSharePermission(notes, id) || SHARE_PERMISSION_VIEW,
            revision: notes.revision,
            richText: notes.richText,
            text: notes.text }, id) || failed;
    });
    if (failed) {
        dispatch(showNotification({ titleKey: 'notesPane.shareFailed' }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
    }
}

/**
 * Whether a received notes document is well formed. It comes from another
 * participant, so it is checked before it reaches an editor: bounded JSON,
 * plain-text body, and a Delta whose ops are all string inserts with optional
 * plain-object attributes — embeds never reach Quill at all.
 *
 * @param {Object} data - The message.
 * @returns {boolean}
 */
function isValidSharedNotesDoc({ richText, text }) {
    if (typeof text !== 'string') {
        return false;
    }
    if (richText !== null && richText !== undefined) {
        if (typeof richText !== 'object' || Array.isArray(richText) || !Array.isArray(richText.ops)) {
            return false;
        }
        for (const op of richText.ops) {
            if (!op || typeof op !== 'object' || Array.isArray(op)) {
                return false;
            }
            if (typeof op.insert !== 'string') {
                return false;
            }
            if ('attributes' in op
                    && (op.attributes === null || typeof op.attributes !== 'object' || Array.isArray(op.attributes))) {
                return false;
            }
        }
    }
    try {
        return JSON.stringify({ richText,
            text }).length <= MAX_SHARED_NOTES_LENGTH;
    } catch {
        return false;
    }
}

/**
 * Reads the permission and revision of an incoming notes-share. Legacy
 * messages without either field stay view-only; unknown values or a malformed
 * revision reject the message, and an edit grant without a revision epoch can
 * never be exercised so it is treated as view.
 *
 * @param {Object} data - The message.
 * @returns {?Object} { permission, revision }, or null when malformed.
 */
function parseShareMessage(data) {
    let permission = data.permission;
    let revision = data.revision;

    if (permission === undefined || permission === null) {
        permission = SHARE_PERMISSION_VIEW;
    } else if (permission !== SHARE_PERMISSION_VIEW && permission !== SHARE_PERMISSION_EDIT) {
        return null;
    }
    if (revision === undefined || revision === null) {
        revision = null;
    } else if (!Number.isSafeInteger(revision) || revision < 0) {
        return null;
    }
    if (permission === SHARE_PERMISSION_EDIT && revision === null) {
        permission = SHARE_PERMISSION_VIEW;
    }

    return { permission,
        revision };
}

/**
 * Handles a recipient's edit request on the owner's side. The sender must hold
 * a current edit grant, the payload must be a well-formed document, and the
 * base revision must still be ours — stale, revoked or unauthorized requests
 * never touch our notes, and only the authorized ones get the document back.
 *
 * @param {Store} store - Redux store.
 * @param {string} participantId - The verified sender.
 * @param {Object} data - The notes-edit message.
 * @returns {void}
 */
function handleNotesEdit({ dispatch, getState }, participantId, data) {
    const state = getState();
    const notes = state['features/notes'];
    const conference = getCurrentConference(state);
    const requestId = typeof data.requestId === 'string' && data.requestId.length > 0 && data.requestId.length <= 80
        ? data.requestId : null;

    if (!conference || !requestId) {
        return;
    }
    const reject = () => safeSendMessage(conference, { type: NOTES_EDIT_RESULT_MESSAGE,
        requestId,
        status: EDIT_RESULT_REJECTED }, participantId);

    if (!isValidSharedNotesDoc(data) || !Number.isSafeInteger(data.revision) || data.revision < 0) {
        reject();

        return;
    }
    if (getSharePermission(notes, participantId) !== SHARE_PERMISSION_EDIT) {
        reject();

        return;
    }
    let doc = { richText: data.richText ?? null,
        text: data.text };

    if (data.revision !== notes.revision && sentRevisions.has(data.revision)
            && !appliedEditRequests.has(requestId)) {
        // We moved on since the sender's base: merge rather than conflict, so
        // simultaneous editing keeps both sides' changes.
        doc = mergeNotes(sentRevisions.get(data.revision), doc, notes);
    } else if (data.revision !== notes.revision) {
        // Authorized but stale: verdict plus the authoritative document so the
        // sender can rebase; rejected requests get no content.
        safeSendMessage(conference, { type: NOTES_EDIT_RESULT_MESSAGE,
            requestId,
            revision: notes.revision,
            status: EDIT_RESULT_CONFLICT }, participantId);
        sendSharedNotesTo({ dispatch,
            getState }, participantId);

        return;
    }

    // Accepted edits go through the normal edit path so persistence, revision
    // and sharing stay consistent, then the authoritative doc goes out now.
    appliedEditRequests.add(requestId);
    dispatch(setNotesText(doc.text, doc.richText));
    sendSharedNotes({ dispatch,
        getState });
    safeSendMessage(conference, { type: NOTES_EDIT_RESULT_MESSAGE,
        requestId,
        revision: getState()['features/notes'].revision,
        status: EDIT_RESULT_ACCEPTED }, participantId);
}

/**
 * Handles the owner's verdict on one of our edit requests. Only a pending
 * request with a matching requestId from that owner is updated — late acks
 * and acks from anyone else are ignored.
 *
 * @param {Store} store - Redux store.
 * @param {string} participantId - The verified sender (the owner).
 * @param {Object} data - The notes-edit-result message.
 * @returns {void}
 */
function handleNotesEditResult({ dispatch, getState }, participantId, data) {
    const request = getState()['features/notes'].editRequests[participantId];
    const status = [ EDIT_RESULT_ACCEPTED, EDIT_RESULT_CONFLICT, EDIT_RESULT_REJECTED ].includes(data.status)
        ? data.status : null;
    const requestId = typeof data.requestId === 'string' ? data.requestId : null;

    // Only a verdict matching our pending request matters. Ack for anything
    // else — resolved, timed out, or already accepted and waiting on the
    // authoritative share — is ignored entirely, timer included.
    if (!status || !requestId || !request || request.requestId !== requestId
            || request.status !== EDIT_RESULT_PENDING) {
        return;
    }

    // An 'accepted' verdict is only meaningful with the resulting revision —
    // without it we cannot tell whether a share covers the edit.
    if (status === EDIT_RESULT_ACCEPTED
            && !(Number.isSafeInteger(data.revision) && data.revision >= 0)) {
        return;
    }
    if (status !== EDIT_RESULT_ACCEPTED) {
        // The request resolved; only an acceptance waits on the share too.
        clearEditTimer(participantId);
    }
    dispatch({ type: SHARED_NOTES_EDIT_RESULT,
        ownerId: participantId,
        requestId,
        revision: Number.isSafeInteger(data.revision) && data.revision >= 0 ? data.revision : null,
        status });
}

/**
 * Writes the captured room/document, even if the conference has since changed.
 * Failed writes remain pending for retry instead of being replaced on reopen.
 *
 * @param {Store} store - Redux store.
 * @returns {void}
 */
function flushNotesSave({ dispatch, getState }) {
    clearTimeout(saveTimer);
    saveTimer = undefined;
    for (const [ roomName, { text, richText } ] of pendingSaves) {
        const saved = saveConferenceNotes(roomName, text, richText);

        if (saved) {
            pendingSaves.delete(roomName);
        }
        if (getState()['features/notes'].roomName === roomName) {
            dispatch({ type: NOTES_SAVE_STATUS,
                saveStatus: saved ? 'saved' : 'error' });
        }
    }
}

/**
 * Middleware which keeps the notes pane exclusive with the other side panes,
 * persists notes to local storage, and wires the native notes modal.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const { dispatch, getState } = store;
    const sharedWithBefore = getState()['features/notes']?.sharedWith ?? [];

    if (action.type === LOAD_CONFERENCE_NOTES) {
        flushNotesSave(store);
        const current = getState()['features/notes'];

        // Reopening the same room must not replace the current buffer with an
        // older read made before the pending write (or a failed storage read).
        if (current.roomName === action.roomName && current.saveStatus !== 'loadError') {
            return;
        }
    }

    let nextAction = action;

    if (action.type === LOAD_CONFERENCE_NOTES) {
        const buffered = pendingSaves.get(action.roomName);

        nextAction = { ...action,
            ...buffered
                ? { ...buffered,
                    saveStatus: 'error' } : loadConferenceNotes(action.roomName) };
    } else if (action.type === SET_NOTES_SHARED_WITH) {
        // Normalization boundary: only live remote non-fake participants of
        // this room can be recipients, ids are deduplicated, and only an
        // explicit 'edit' grants edit — anything else stays view-only.
        const remote = getShareableIds(getState());
        const audience = action.audience === SHARE_AUDIENCE_EVERYONE
            ? SHARE_AUDIENCE_EVERYONE : SHARE_AUDIENCE_SELECTED;
        const participantIds = audience === SHARE_AUDIENCE_EVERYONE
            ? remote
            : [ ...new Set(Array.isArray(action.participantIds) ? action.participantIds : []) ]
                .filter(id => remote.includes(id));
        const permissions = {};

        if (audience === SHARE_AUDIENCE_SELECTED) {
            participantIds.forEach(id => {
                permissions[id] = action.permissions?.[id] === SHARE_PERMISSION_EDIT
                    ? SHARE_PERMISSION_EDIT : SHARE_PERMISSION_VIEW;
            });
        }
        nextAction = { ...action,
            audience,
            everyonePermission: action.everyonePermission === SHARE_PERMISSION_EDIT
                ? SHARE_PERMISSION_EDIT : SHARE_PERMISSION_VIEW,
            participantIds,
            permissions };
    }
    const result = next(nextAction);

    switch (action.type) {
    case NOTES_PANE_OPEN: {
        dispatch(closeParticipantsPane());
        dispatch(closeModerationPane());
        dispatch(closeChat());
        dispatch(isClickandPledge(false));
        dispatch(loadNotesFromStorage(getNotesRoomName(getState())));
        break;
    }

    case PARTICIPANTS_PANE_OPEN:
    case MODERATION_PANE_OPEN:
    case OPEN_CHAT: {
        if (getNotesPaneOpen(getState())) {
            dispatch(closeNotesPane());
        }
        break;
    }

    case OPEN_PARTICIPANTS_PANE_MODAL: {
        if (getNotesModalOpen(getState())) {
            dispatch(closeNotesModal());
        }
        break;
    }

    case IS_ENABLED: {
        if (action.payload === true && getNotesPaneOpen(getState())) {
            dispatch(closeNotesPane());
        }
        break;
    }

    case SET_NOTES_TEXT: {
        clearTimeout(saveTimer);
        const roomName = getState()['features/notes'].roomName || getNotesRoomName(getState());

        pendingSaves.set(roomName, {
            text: action.text || '',
            richText: action.richText || null
        });
        saveTimer = setTimeout(() => flushNotesSave(store), SAVE_DEBOUNCE_MS);
        if (getState()['features/notes'].sharedWith.length && !shareTimer) {
            shareTimer = setTimeout(() => sendSharedNotes(store), SHARE_THROTTLE_MS);
        }
        break;
    }

    case SET_NOTES_SHARED_WITH: {
        const conference = getCurrentConference(getState());
        const notes = getState()['features/notes'];
        let failed = false;

        // A stopped share re-arms the size warning for the next one.
        if (!notes.sharedWith.length) {
            shareTooLargeShown = false;
        }
        sharedWithBefore
            .filter(id => !notes.sharedWith.includes(id))
            .forEach(id => {
                failed = !safeSendMessage(conference, { type: NOTES_UNSHARE_MESSAGE }, id) || failed;
            });
        if (failed) {
            dispatch(showNotification({ titleKey: 'notesPane.shareFailed' }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        }
        sendSharedNotes(store);
        break;
    }

    case SHARED_NOTES_EDIT_SUBMIT: {
        const state = getState();
        const conference = getCurrentConference(state);
        const share = state['features/notes'].sharedNotes[action.participantId];
        const fail = status => dispatch({ type: SHARED_NOTES_EDIT_RESULT,
            ownerId: action.participantId,
            requestId: action.requestId,
            revision: share?.revision ?? null,
            status });

        // Only a live edit-granted share based on the latest known revision can
        // be submitted; otherwise the request fails or conflicts locally
        // without ever reaching the wire.
        if (!conference || !getShareableIds(state).includes(action.participantId)) {
            fail(EDIT_RESULT_FAILED);
        } else if (!share || share.permission !== SHARE_PERMISSION_EDIT) {
            fail(EDIT_RESULT_REJECTED);
        } else if (share.revision !== action.revision) {
            fail(EDIT_RESULT_CONFLICT);
        } else if (!isValidSharedNotesDoc(action)
                || !safeSendMessage(conference, { type: NOTES_EDIT_MESSAGE,
                    requestId: action.requestId,
                    revision: action.revision,
                    richText: action.richText ?? null,
                    text: action.text }, action.participantId)) {
            fail(EDIT_RESULT_FAILED);
        } else {
            // An unanswered request fails after the timeout — including an
            // 'accepted' verdict whose authoritative share never arrives.
            clearEditTimer(action.participantId);
            editTimers.set(action.participantId, setTimeout(() => {
                editTimers.delete(action.participantId);
                const request = getState()['features/notes'].editRequests[action.participantId];

                if (request?.requestId === action.requestId
                        && (request.status === EDIT_RESULT_PENDING
                            || request.status === EDIT_RESULT_ACCEPTED)) {
                    dispatch({ type: SHARED_NOTES_EDIT_RESULT,
                        ownerId: action.participantId,
                        requestId: action.requestId,
                        status: EDIT_RESULT_FAILED });
                }
            }, EDIT_TIMEOUT_MS));
        }
        break;
    }

    case PARTICIPANT_LEFT: {
        clearEditTimer(action.participant?.id);
        break;
    }

    case PARTICIPANT_JOINED: {
        const notes = getState()['features/notes'];
        const id = action.participant?.id;

        // 'Everyone' covers late joiners with the current share.
        if (id && notes.shareAudience === SHARE_AUDIENCE_EVERYONE && notes.sharedWith.includes(id)) {
            sendSharedNotesTo(store, id);
        }
        break;
    }

    case ENDPOINT_MESSAGE_RECEIVED: {
        const participantId = getValidMessageSender(getState(), action.participant);
        const { data } = action;

        if (!participantId || !data || typeof data !== 'object' || Array.isArray(data)) {
            break;
        }
        switch (data.type) {
        case NOTES_UNSHARE_MESSAGE:
            clearEditTimer(participantId);
            dispatch({ type: SHARED_NOTES_REMOVED,
                participantId });
            break;

        case NOTES_SHARE_MESSAGE: {
            const share = parseShareMessage(data);

            if (!share || !isValidSharedNotesDoc(data)) {
                break;
            }
            const isNewShare = !getState()['features/notes'].sharedNotes[participantId];

            dispatch({ type: SHARED_NOTES_RECEIVED,
                participantId,
                permission: share.permission,
                revision: share.revision,
                richText: data.richText ?? null,
                text: data.text });

            // If this share settled an accepted request (or dropped the grant
            // mid-flight), its timeout goes too.
            if (!getState()['features/notes'].editRequests[participantId]) {
                clearEditTimer(participantId);
            }
            if (isNewShare) {
                dispatch(showNotification({
                    titleArguments: { name: getParticipantDisplayName(getState(), participantId) },
                    titleKey: 'notesPane.sharedWithYou',
                    customActionNameKey: 'notesPane.view',
                    customActionHandler: () => {
                        dispatch(checkIfReactNative() ? openNotesModal() : openNotesPane());
                        dispatch(viewSharedNotes(participantId));

                        return true;
                    }
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            break;
        }

        case NOTES_EDIT_MESSAGE:
            handleNotesEdit(store, participantId, data);
            break;

        case NOTES_EDIT_RESULT_MESSAGE:
            handleNotesEditResult(store, participantId, data);
            break;
        }
        break;
    }

    case FLUSH_NOTES:
    case NOTES_PANE_CLOSE:
    case CLOSE_NOTES_MODAL: {
        flushNotesSave(store);
        if (action.type === CLOSE_NOTES_MODAL
                && getState()['features/base/modal']?.activeModalId === NOTES_MODAL_ID) {
            dispatch(setActiveModalId());
        }
        break;
    }

    case OPEN_NOTES_MODAL: {
        dispatch(closeParticipantsPaneModal());
        dispatch(setActiveModalId(NOTES_MODAL_ID));
        dispatch(loadNotesFromStorage(getNotesRoomName(getState())));
        break;
    }

    case CONFERENCE_JOINED: {
        clearTimeout(shareTimer);
        shareTimer = undefined;
        clearEditTimers();
        sentRevisions.clear();
        appliedEditRequests.clear();
        dispatch(loadNotesFromStorage(getNotesRoomName(getState())));
        break;
    }

    case CONFERENCE_LEFT: {
        clearTimeout(shareTimer);
        shareTimer = undefined;
        clearEditTimers();
        sentRevisions.clear();
        appliedEditRequests.clear();
        flushNotesSave(store);
        break;
    }
    }

    return result;
});
